import { addDays, addHours, addMonths, addWeeks, addYears, endOfDay, format, isSameYear } from 'date-fns';
import { DurationPreset, PRESET_MAP } from '@/utils/guest-access/constants.ts';
import { type DurationKey } from '@/components/common/access-time-selector/types.ts';
import { ACCESS_TIME_SLOT_MINUTES, LAST_ACCESS_TIME_SLOT } from '@/utils/access/timeSlots';

export function ceilToNextTimeSlot(date: Date): Date {
	const d = new Date(date);
	const minutes = d.getMinutes();
	const remainder = minutes % ACCESS_TIME_SLOT_MINUTES;
	if (remainder !== 0 || d.getSeconds() !== 0 || d.getMilliseconds() !== 0) {
		const add = ACCESS_TIME_SLOT_MINUTES - remainder;
		d.setMinutes(minutes + add, 0, 0);
	} else {
		// Already on a time-slot boundary, just zero smaller units.
		d.setSeconds(0, 0);
	}
	return d;
}

export function dateToSlot(d: Date): number {
	const minutes = d.getHours() * 60 + d.getMinutes();
	return Math.min(LAST_ACCESS_TIME_SLOT, Math.max(0, Math.floor(minutes / ACCESS_TIME_SLOT_MINUTES)));
}

/**
 * Calculate target date from a duration preset
 */
export function calculatePresetTargetDate(preset: DurationPreset, baseDate: Date = new Date()): Date {
	switch (preset.unit) {
		case 'hours':
			return addHours(baseDate, preset.value);
		case 'days':
			return addDays(baseDate, preset.value);
		case 'weeks':
			return addWeeks(baseDate, preset.value);
		case 'months':
			return addMonths(baseDate, preset.value);
		case 'years':
			return addYears(baseDate, preset.value);
		default:
			return addHours(baseDate, preset.value);
	}
}

export function composeLocalDate(dateString: string, timeString: string): Date {
	const [year, month, day] = dateString.split('-').map(Number);
	const [hours, minutes] = timeString.split(':').map(Number);
	return new Date(year, month - 1, day, hours, minutes);
}

export function computeExpiryDate(key: DurationKey, customDate?: string, customTime?: string): Date | null {
	if (key === 'indefinite') return null;
	if (key === 'today') return endOfDay(new Date());
	if (key === 'tomorrow') return endOfDay(addDays(new Date(), 1));
	if (key === 'custom') {
		if (!customDate) return addDays(new Date(), 1);
		return composeLocalDate(customDate, customTime || '12:00');
	}

	const preset = PRESET_MAP[key];
	return ceilToNextTimeSlot(calculatePresetTargetDate(preset, new Date()));
}

export function formatExpirySummary(date: Date): string {
	return format(date, isSameYear(date, new Date()) ? 'd MMM, h:mm a' : 'd MMM yyyy, h:mm a');
}

export function getExpiryDisplay(key: DurationKey, customDate?: string, customTime?: string): string {
	if (key === 'indefinite') return 'Only when removed';
	if (key === 'custom') {
		if (!customDate) return 'Select date';
		return formatExpirySummary(composeLocalDate(customDate, customTime || '12:00'));
	}

	const date = computeExpiryDate(key);
	if (!date) return 'Only when removed';
	return formatExpirySummary(date);
}

export function inferDurationKey(expiresAt: Date | null, allowIndefinite: boolean): DurationKey {
	if (expiresAt === null && allowIndefinite) return 'indefinite';
	if (!expiresAt) return 'today';

	const now = new Date();
	const todayEnd = endOfDay(now);
	if (Math.abs(expiresAt.getTime() - todayEnd.getTime()) < 1000) {
		return 'today';
	}
	const tomorrowEnd = endOfDay(addDays(now, 1));
	if (Math.abs(expiresAt.getTime() - tomorrowEnd.getTime()) < 1000) {
		return 'tomorrow';
	}

	for (const [key, preset] of Object.entries(PRESET_MAP) as [
		Exclude<DurationKey, 'indefinite' | 'custom'>,
		DurationPreset,
	][]) {
		if (key === 'today' || key === 'tomorrow') continue;
		const expected = ceilToNextTimeSlot(calculatePresetTargetDate(preset, now));
		if (Math.abs(expiresAt.getTime() - expected.getTime()) < 1000) {
			return key;
		}
	}

	return 'custom';
}

/**
 * Calculate valid time slot for a given date, respecting minimum slot constraints for today
 */
export function calculateValidSlot(targetDate: Date, currentSlot: number, today: Date = new Date()): number {
	const isToday =
		targetDate.getFullYear() === today.getFullYear() &&
		targetDate.getMonth() === today.getMonth() &&
		targetDate.getDate() === today.getDate();
	const minSlot = isToday ? dateToSlot(ceilToNextTimeSlot(today)) : 0;
	return Math.max(minSlot, Math.min(LAST_ACCESS_TIME_SLOT, currentSlot));
}
